<template>
  <div class="min-h-screen bg-background">
    <Header />

    <main>
      <!-- Hero Section -->
      <section class="border-b border-border bg-linear-to-br from-bubba-green-light via-secondary/5 to-bubba-cream py-12 md:py-16">
        <div class="container">
          <div class="mx-auto max-w-3xl text-center">
            <h1 class="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
              {{ $ts('providers.title') }}
            </h1>
            <p class="mt-4 text-lg text-muted-foreground">
              {{ $ts('providers.subtitle') }}
            </p>
          </div>
        </div>
      </section>

      <!-- Filters -->
      <section class="border-b border-border bg-card">
        <div class="container py-4">
          <div class="flex flex-wrap gap-2">
            <button
              v-for="filter in serviceFilters"
              :key="filter.value"
              :class="[
                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                activeFilter === filter.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80',
              ]"
              @click="activeFilter = filter.value"
            >
              {{ $ts(filter.labelKey) }}
            </button>
          </div>
        </div>
      </section>

      <!-- Results -->
      <section class="py-8">
        <div class="container">
          <!-- Error State -->
          <div v-if="status !== 'pending' && error" class="flex flex-col items-center justify-center py-20">
            <div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <Icon name="tabler:alert-circle" class="text-destructive" size="40" />
            </div>
            <p class="text-lg font-medium text-foreground">Something went wrong</p>
            <p class="mt-1 text-muted-foreground">{{ error.message }}</p>
          </div>

          <Skeleton v-else name="website-providers-list" :loading="status === 'pending'">
            <template v-if="providers">
              <!-- Empty State -->
              <div v-if="filteredProviders.length === 0" class="flex flex-col items-center justify-center py-20">
                <div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                  <Icon name="tabler:building-store" class="text-muted-foreground" size="40" />
                </div>
                <h2 class="text-xl font-semibold text-foreground">{{ $ts('providers.noResults.title') }}</h2>
                <p class="mt-2 text-muted-foreground">{{ $ts('providers.noResults.description') }}</p>
              </div>

              <!-- Provider Cards -->
              <div v-else class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <NuxtLink
                  v-for="provider in filteredProviders"
                  :key="provider._id"
                  :to="`/p/${provider.slug ?? provider._id}`"
                  class="group"
                >
                  <Card class="h-full overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
                    <CardContent class="p-6">
                      <!-- Provider Icon & Name -->
                      <div class="flex items-start gap-4">
                        <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                          <Icon :name="getServiceIcon(provider.serviceTypes[0])" class="text-primary" size="28" />
                        </div>
                        <div class="min-w-0 flex-1">
                          <h3 class="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                            {{ provider.businessName }}
                          </h3>
                          <!-- Rating -->
                          <div class="mt-1 flex items-center gap-1.5">
                            <template v-if="provider.averageRating">
                              <Icon name="tabler:star-filled" class="text-amber-400" size="16" />
                              <span class="text-sm font-medium text-foreground">{{ provider.averageRating.toFixed(1) }}</span>
                              <span class="text-sm text-muted-foreground">({{ provider.reviewCount }} {{ $ts('providers.reviews') }})</span>
                            </template>
                            <span v-else class="text-sm text-muted-foreground">{{ $ts('providers.noReviews') }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Description -->
                      <p v-if="provider.description" class="mt-4 text-sm text-muted-foreground line-clamp-2">
                        {{ provider.description }}
                      </p>

                      <!-- Service Type Badges -->
                      <div class="mt-4 flex flex-wrap gap-1.5">
                        <Badge
                          v-for="serviceType in provider.serviceTypes"
                          :key="serviceType"
                          variant="secondary"
                          class="text-xs"
                        >
                          {{ formatServiceType(serviceType) }}
                        </Badge>
                        <Badge v-if="provider.isVerified" variant="default" class="text-xs gap-1">
                          <Icon name="tabler:shield-check" size="12" />
                          {{ $ts('providers.verified') }}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </NuxtLink>
              </div>
            </template>

            <template #fallback>
              <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div
                  v-for="i in 6"
                  :key="i"
                  class="h-64 animate-pulse rounded-2xl bg-muted"
                />
              </div>
            </template>
          </Skeleton>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n, useSeoMeta, useAsyncData, useSchemaOrg, defineWebPage, useHead, useRuntimeConfig, useRoute } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import Card from '#bubba/shared/components/ui/card/Card.vue';
import CardContent from '#bubba/shared/components/ui/card/CardContent.vue';
import Badge from '#bubba/shared/components/ui/badge/Badge.vue';
import Skeleton from 'boneyard-js/vue';
import type { BasePartnerResult } from '#types/partner';

definePageMeta({
  layout: false,
});

const { $ts } = useI18n();

const route = useRoute();
const { public: { siteUrl } } = useRuntimeConfig();
const locale = computed(() => route.params.locale as string | undefined);
const canonicalUrl = computed(() => {
  const prefix = locale.value && locale.value !== 'en' ? `/${locale.value}` : '';
  return `${siteUrl || 'https://bubba.pet'}${prefix}/providers`;
});

useSeoMeta({
  title: () => $ts('providers.seo.title'),
  description: () => $ts('providers.seo.description'),
});

useHead({
  link: [
    { rel: 'canonical', href: () => canonicalUrl.value },
  ],
});

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Providers' },
]);

interface Provider extends BasePartnerResult {
  isVerified: boolean;
  averageRating: number | null;
  reviewCount: number;
}

const activeFilter = ref('all');

const serviceFilters = [
  { value: 'all', labelKey: 'providers.filterAll' },
  { value: 'vet', labelKey: 'providers.filterVet' },
  { value: 'grooming', labelKey: 'providers.filterGrooming' },
  { value: 'boarding', labelKey: 'providers.filterBoarding' },
  { value: 'training', labelKey: 'providers.filterTraining' },
  { value: 'walking', labelKey: 'providers.filterWalking' },
  { value: 'daycare', labelKey: 'providers.filterDaycare' },
  { value: 'online_vet', labelKey: 'providers.filterOnlineVet' },
];

const { data: providers, error, status } = await useAsyncData<Provider[]>(
  'providers-list',
  // @ts-expect-error TS2321 - excessive stack depth from Nitro route type resolution
  () => $fetch('/api/business') as Promise<Provider[]>,
);

const filteredProviders = computed(() => {
  if (!providers.value) return [];
  if (activeFilter.value === 'all') return providers.value;
  return providers.value.filter(p =>
    p.serviceTypes.includes(activeFilter.value),
  );
});

function getServiceIcon(serviceType: string | undefined): string {
  const iconMap: Record<string, string> = {
    vet: 'tabler:stethoscope',
    grooming: 'tabler:scissors',
    boarding: 'tabler:home',
    walking: 'tabler:shoe',
    daycare: 'tabler:sun',
    training: 'tabler:school',
    petsitting: 'tabler:heart',
    transport: 'tabler:car',
    online_vet: 'tabler:device-laptop',
  };
  return iconMap[serviceType ?? ''] ?? 'tabler:paw';
}

function formatServiceType(type: string): string {
  const labels: Record<string, string> = {
    vet: 'Veterinary',
    grooming: 'Grooming',
    boarding: 'Boarding',
    walking: 'Dog Walking',
    daycare: 'Daycare',
    training: 'Training',
    petsitting: 'Pet Sitting',
    transport: 'Transport',
    online_vet: 'Online Vet',
  };
  return labels[type] ?? type;
}
</script>
